import { useCallback } from "react";

import type { DocumentType } from "../DocumentTypes";

// MSA service types that all use msa_id field
// TODO keep in sync with app-server/future_medical/constants.py MSA_SERVICE_TYPES
export const MSA_SERVICE_TYPES = ["MSA", "MSA-111", "NTMSA", "PRE-MSA", "ARV"];

// Service types a document can be directly linked to. Shared so the upload
// gate and this selector can't drift apart.
export const LINKABLE_SERVICE_TYPES = [
  ...MSA_SERVICE_TYPES,
  "ARS",
  "LCP",
  "MCP",
  "LIEN",
  "DOCS",
];

type Lien = {
  id: number;
  case_number: string;
  part_type: string;
  part_type_verbose: string;
};

export type FileService = {
  id: number;
  type: string;
  type_verbose: string;
  label: string;
  msa?: number | null;
  costprojection?: number | null;
  lien_file?: number | null;
  document_management_file?: number | null;
  amended_review_screening?: number | null;
  workspace_url?: string | null;
  liens?: Lien[];
};

// Whether the service's real record exists yet, so callers know whether
// to use the real FK or the pending service_id.
export const hasProvisionedEntityId = (service: FileService): boolean => {
  if (MSA_SERVICE_TYPES.includes(service.type)) return service.msa != null;
  if (service.type === "LCP" || service.type === "MCP")
    return service.costprojection != null;
  if (service.type === "ARS") return service.amended_review_screening != null;
  if (service.type === "DOCS") return service.document_management_file != null;
  if (service.type === "LIEN") return service.lien_file != null;
  return false;
};

// Parse an individual-lien option value (`lien-{lienId}`); null for any
// other option value, including a malformed one
export const parseLienOptionValue = (value: string): number | null => {
  if (!value.startsWith("lien-")) return null;
  const lienId = parseInt(value.substring(5));
  return isNaN(lienId) ? null : lienId;
};

// Option list shared with the upload gate so both dropdowns show the same
// identifiers (MSA/cost projection/ARS ids, lien case numbers) to tell apart
// services of the same kind. Option values are the service id, or
// `lien-{lienId}` for an individual lien within a lien file.
export const renderServiceOptions = (services: FileService[]): JSX.Element[] =>
  services.flatMap((service) => {
    // Flag options whose real record doesn't exist yet
    const pendingSuffix = hasProvisionedEntityId(service) ? "" : " (pending)";

    // for LIEN services, show the lien file option plus individual liens.
    // The individual options never need the pending suffix: a lien record
    // only exists under an already-created lien file
    if (service.type === "LIEN") {
      return [
        // lien file option
        <option key={service.id} value={service.id}>
          {service.label} - Lien File
          {pendingSuffix}
        </option>,
        // individual lien options
        ...(service.liens ?? []).map((lien) => (
          <option key={`lien-${lien.id}`} value={`lien-${lien.id}`}>
            {service.label} - {lien.part_type_verbose} ({lien.case_number})
          </option>
        )),
      ];
    }

    // for other services, show normal options
    return (
      <option key={service.id} value={service.id}>
        {service.label}
        {MSA_SERVICE_TYPES.includes(service.type) && service.msa
          ? ` (${service.msa})`
          : ""}
        {(service.type === "LCP" || service.type === "MCP") &&
        service.costprojection
          ? ` (${service.costprojection})`
          : ""}
        {service.type === "ARS" && service.amended_review_screening
          ? ` (${service.amended_review_screening})`
          : ""}
        {pendingSuffix}
      </option>
    );
  });

type ServiceSelectorProps = {
  document: DocumentType;
  fileServices: FileService[];
  isUpdating: boolean;
  onServiceChange: (
    document: DocumentType,
    serviceId: number | null,
    lienId?: number | null,
  ) => void;
};

const ServiceSelector = ({
  document,
  fileServices,
  isUpdating,
  onServiceChange,
}: ServiceSelectorProps) => {
  // determine current selection from the document
  // returns either "serviceId" or "lien-{lienId}" for individual liens
  const getCurrentSelection = useCallback(() => {
    if (document.msa_id) {
      // msa_id is used by all MSA service types (MSA, MSA-111, NTMSA, PRE-MSA, ARV)
      return fileServices
        .find(
          (s) =>
            MSA_SERVICE_TYPES.includes(s.type) && s.msa === document.msa_id,
        )
        ?.id?.toString();
    }
    if (document.cost_projection_id) {
      return fileServices
        .find(
          (s) =>
            (s.type === "LCP" || s.type === "MCP") &&
            s.costprojection === document.cost_projection_id,
        )
        ?.id?.toString();
    }
    if (document.amended_review_screening_id) {
      // ARS uses amended_review_screening_id
      return fileServices
        .find(
          (s) =>
            s.type === "ARS" &&
            s.amended_review_screening === document.amended_review_screening_id,
        )
        ?.id?.toString();
    }
    if (document.document_management_file_id) {
      return fileServices
        .find(
          (s) =>
            s.type === "DOCS" &&
            s.document_management_file === document.document_management_file_id,
        )
        ?.id?.toString();
    }
    if (document.miscellaneous_service_id) {
      return fileServices
        .find(
          (s) =>
            (s.type === "CON" || s.type === "AUD") &&
            s.id === document.miscellaneous_service_id,
        )
        ?.id?.toString();
    }
    // check for individual lien first
    if (document.lien_id) {
      return `lien-${document.lien_id}`;
    }
    // then check for lien file
    if (document.lien_file) {
      return fileServices
        .find((s) => s.type === "LIEN" && s.lien_file === document.lien_file)
        ?.id?.toString();
    }
    // Pending link: the service's underlying record doesn't exist yet
    if (document.service_id) {
      return fileServices
        .find((s) => s.id === document.service_id)
        ?.id?.toString();
    }
    return undefined;
  }, [document, fileServices]);

  const currentSelection = getCurrentSelection();

  // Still selectable even without a real entity yet; onServiceChange
  // decides whether to send a real FK or a pending service_id
  const availableServices = fileServices.filter((service) =>
    LINKABLE_SERVICE_TYPES.includes(service.type),
  );

  const hasNoServices = availableServices.length === 0;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const value = e.target.value;
    const lienId = parseLienOptionValue(value);
    if (lienId != null) {
      // individual lien selected; find the lien file service that contains it
      const lienFileService = availableServices.find(
        (s) => s.type === "LIEN" && s.liens?.some((l) => l.id === lienId),
      );
      if (lienFileService) {
        onServiceChange(document, lienFileService.id, lienId);
      } else {
        // Stale selection (services changed since render); drop the change
        // rather than linking wrongly, matching the upload gate
        console.error("Lien service not found for selected lien:", lienId);
      }
    } else {
      // service selected (not an individual lien)
      const newServiceId = parseInt(value);
      if (!isNaN(newServiceId)) {
        onServiceChange(document, newServiceId, null);
      }
    }
  };

  return (
    <select
      className="document-type-select"
      value={currentSelection || ""}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      onChange={handleChange}
      disabled={isUpdating || hasNoServices}
    >
      {hasNoServices ? (
        <option value="">No services</option>
      ) : !currentSelection ? (
        // Keeps the controlled select valid while nothing is picked yet;
        // disappears once a service is set, so it can't be picked back to
        <option value="" disabled>
          Select service
        </option>
      ) : null}
      {renderServiceOptions(availableServices)}
    </select>
  );
};

export default ServiceSelector;
